import React, { useState } from 'react';
import { CreditCard, Smartphone, Loader2 } from 'lucide-react';
import { isAppleWalletSupported, isGoogleWalletSupported, addToAppleWallet, addToGoogleWallet } from '../utils/wallet';
import { getApiBaseUrl } from '../api/config';
import { toast } from 'react-hot-toast';
import type { ShopTheme } from '../theme';
import { useI18n } from '../i18n';

interface WalletButtonsProps {
  voucherCode: string;
  /** Venue theme, so the wallet buttons sit in the same paper-and-ink system as the card. */
  theme: ShopTheme;
  className?: string;
}

const WalletButtons: React.FC<WalletButtonsProps> = ({ voucherCode, theme, className = '' }) => {
  const { t } = useI18n();
  const [loadingApple, setLoadingApple] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [appleSupported, setAppleSupported] = useState(false);
  const [googleSupported, setGoogleSupported] = useState(false);
  const [appleConfigured, setAppleConfigured] = useState<boolean | null>(null);

  React.useEffect(() => {
    setAppleSupported(isAppleWalletSupported());
    setGoogleSupported(isGoogleWalletSupported());
    
    // Check if Apple Wallet is configured
    checkAppleWalletConfig();
  }, []);

  const checkAppleWalletConfig = async () => {
    if (!voucherCode) {
      setAppleConfigured(false);
      return;
    }
    
    try {
      const apiBaseUrl = getApiBaseUrl();
      const url = `${apiBaseUrl}/gift-vouchers/public/${encodeURIComponent(voucherCode)}/wallet/apple`;
      
      // Try a HEAD request first (lighter)
      const headResponse = await fetch(url, { method: 'HEAD' });
      if (headResponse.ok) {
        setAppleConfigured(true);
        return;
      }
      
      // If HEAD doesn't work, try GET to check error
      const getResponse = await fetch(url);
      if (getResponse.ok) {
        setAppleConfigured(true);
      } else {
        const data = await getResponse.json();
        // Hide button if service is unavailable (not configured)
        setAppleConfigured(data.error?.code !== 'service_unavailable');
      }
    } catch {
      // On error, assume not configured
      setAppleConfigured(false);
    }
  };

  const handleAppleWallet = async () => {
    if (!voucherCode) {
      toast.error(t('wallet.code_required'));
      return;
    }

    try {
      setLoadingApple(true);
      await addToAppleWallet(voucherCode);
      // On iOS, the browser will handle the .pkpass file automatically
      toast.success(t('wallet.opening_apple'));
    } catch (error) {
      console.error('Error adding to Apple Wallet:', error);
      toast.error(error instanceof Error ? error.message : t('wallet.apple_failed'));
    } finally {
      setLoadingApple(false);
    }
  };

  const handleGoogleWallet = async () => {
    if (!voucherCode) {
      toast.error(t('wallet.code_required'));
      return;
    }

    try {
      setLoadingGoogle(true);
      const walletUrl = await addToGoogleWallet(voucherCode);
      
      // Open Google Wallet URL in new tab
      window.open(walletUrl, '_blank');
      toast.success(t('wallet.opening_google'));
    } catch (error) {
      console.error('Error adding to Google Wallet:', error);
      toast.error(error instanceof Error ? error.message : t('wallet.google_failed'));
    } finally {
      setLoadingGoogle(false);
    }
  };

  // Don't render if neither wallet is supported (shouldn't happen, but just in case)
  if (!appleSupported && !googleSupported) {
    return null;
  }

  return (
    <div className={`flex flex-col gap-2.5 ${className}`}>
      {appleSupported && appleConfigured === true && (
        <button
          onClick={handleAppleWallet}
          disabled={loadingApple}
          className="inline-flex w-full items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-45"
          style={{
            padding: '14px',
            background: theme.surface,
            color: theme.ink,
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: theme.radius,
            font: `500 14px/1 ${theme.fontFamily}`,
          }}
        >
          {loadingApple ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Smartphone className="h-4 w-4" />
              {t('wallet.apple')}
            </>
          )}
        </button>
      )}

      {googleSupported && (
        <button
          onClick={handleGoogleWallet}
          disabled={loadingGoogle}
          className="inline-flex w-full items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-45"
          style={{
            padding: '14px',
            background: theme.surface,
            color: theme.ink,
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: theme.radius,
            font: `500 14px/1 ${theme.fontFamily}`,
          }}
        >
          {loadingGoogle ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <CreditCard className="h-4 w-4" />
              {t('wallet.google')}
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default WalletButtons;

